import React, { useState } from "react";
import { CalendarDays, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Calendar } from "@/components/ui/calendar";
import api from "@/lib/api";
import { IMG } from "@/data/site";

const SERVICES = ["Free Consultation", "Video Consultation", "Home Visit", "Bathroom Design Consultation", "Tile Consultation", "Site Survey", "Quote Appointment"];
const TIMES = ["09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00"];

export default function Booking() {
  const [service, setService] = useState(SERVICES[0]);
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", postcode: "", projectType: "", message: "" });
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!form.name || !form.email || !date || !time) { toast.error("Please choose a date, time and add your contact details."); return; }
    try {
      await api.post("/enquiries", {
        kind: "booking", name: form.name, email: form.email, phone: form.phone, postcode: form.postcode,
        message: form.message,
        payload: { consultation_type: service, project_type: form.projectType, date: date.toDateString(), time },
      });
      setDone(true);
      toast.success("Booking request sent!");
    } catch (e) { toast.error("Could not send booking. Please try again."); }
  };

  if (done) return (
    <div data-testid="booking-page">
      <PageHero eyebrow="Booking confirmed" title="We've received your request" subtitle="Vincent will confirm your consultation shortly by phone or email." image={IMG.bath[0]} />
      <div className="container-rossi py-20 text-center">
        <Check size={44} className="text-rossi-gold mx-auto" />
        <p className="mt-4 text-rossi-cream/80">{service} on {date?.toDateString()} at {time}.</p>
      </div>
    </div>
  );

  return (
    <div data-testid="booking-page">
      <Seo title="Book a Consultation" description="Book a free consultation, home visit, site survey or design consultation with Rossi Ceramics across Stockport and the North West." />
      <PageHero eyebrow="Let's begin" title="Book your Rossi consultation" subtitle="Choose a service, pick a date and time, and we'll take it from there." image={IMG.bath[0]} />

      <section className="container-rossi py-16 grid lg:grid-cols-2 gap-10 max-w-6xl">
        <div className="space-y-6">
          <div className="card-lux p-7">
            <h2 className="font-serif text-xl text-rossi-cream mb-4">1. Choose a service</h2>
            <div className="flex flex-wrap gap-2">
              {SERVICES.map((s) => (
                <button key={s} onClick={() => setService(s)} data-testid={`booking-service-${s.toLowerCase().replace(/\s+/g, "-")}`} className={`px-4 py-2 rounded-full text-sm ${service === s ? "bg-rossi-gold text-rossi-bg" : "border border-rossi-gold/30 text-rossi-cream/80 hover:bg-rossi-gold/10"}`}>{s}</button>
              ))}
            </div>
          </div>

          <div className="card-lux p-7">
            <h2 className="font-serif text-xl text-rossi-cream mb-4">2. Pick a date & time</h2>
            <div className="flex flex-col sm:flex-row gap-6">
              <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} className="rounded-xl border border-white/10 bg-rossi-bg" data-testid="booking-calendar" />
              <div className="flex flex-wrap sm:flex-col gap-2 content-start">
                {TIMES.map((t) => (
                  <button key={t} onClick={() => setTime(t)} data-testid={`booking-time-${t}`} className={`px-4 py-2 rounded-lg text-sm ${time === t ? "bg-rossi-gold text-rossi-bg" : "border border-white/10 text-rossi-cream/80 hover:border-rossi-gold/40"}`}>{t}</button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="card-lux p-7 h-fit lg:sticky lg:top-24">
          <h2 className="font-serif text-xl text-rossi-cream mb-4">3. Your details</h2>
          <div className="space-y-3">
            {[["name", "Full name"], ["email", "Email"], ["phone", "Phone"], ["postcode", "Postcode"], ["projectType", "Project type (e.g. bathroom, floor)"]].map(([k, ph]) => (
              <input key={k} placeholder={ph} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50" data-testid={`booking-${k}`} />
            ))}
            <textarea placeholder="Tell us about your project (optional)" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50" data-testid="booking-message" />
          </div>
          <div className="mt-4 text-sm text-rossi-cream/70 flex items-center gap-2"><CalendarDays size={15} className="text-rossi-gold" /> {service}{date ? ` · ${date.toDateString()}` : ""}{time ? ` · ${time}` : ""}</div>
          <button onClick={submit} className="btn-gold w-full mt-5" data-testid="booking-submit">Confirm booking <ArrowRight size={16} /></button>
        </div>
      </section>
    </div>
  );
}
